import React, { useMemo, useState } from "react";
import { TbSearch, TbPlus } from "react-icons/tb";
import Modal from "../../../components/Modal/Modal";
import { stringToColor } from "../../../components/CalendarCalendar/calendarHelpers";
import styles from "./AddProfessionalModal.module.css";

const EMPTY_FORM = { name: "", email: "", role: "MEMBER", specialty: "" };
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Nome exibível de um TenantMember (nome → e-mail → fallback). */
function memberName(tm) {
  return tm.user?.name || tm.user?.email || "Profissional";
}

function initialOf(name) {
  return (name || "?").charAt(0).toUpperCase();
}

/**
 * Modal "Adicionar profissional a um setor" (setor-first, F2 frontend).
 *
 * Dois caminhos, em abas:
 *  1. Vincular existente — escolhe um TenantMember do tenant que ainda NÃO está
 *     no setor (a lista `availableMembers` já vem filtrada) → `onLinkExisting`.
 *  2. Criar novo — formulário enxuto (nome, e-mail, função, especialidade); o
 *     pai cria o profissional e, na sequência, vincula ao setor → `onCreateAndLink`.
 *
 * O pai é dono das chamadas de API, dos toasts e do estado `busy`; este
 * componente só coleta a intenção do usuário. Referência de membro é sempre por
 * `TenantMember.id` — nunca telefone/identificador de canal.
 */
export default function AddProfessionalModal({
  department,
  availableMembers,
  onLinkExisting,
  onCreateAndLink,
  onClose,
  busy = false,
}) {
  const [mode, setMode] = useState("existing"); // existing | new
  const [query, setQuery] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState("");

  const titleId = "add-prof-title";

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return availableMembers;
    return availableMembers.filter((m) => {
      const name = (m.user?.name || "").toLowerCase();
      const email = (m.user?.email || "").toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [availableMembers, query]);

  const handleField = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreate = (e) => {
    e.preventDefault();
    const name = form.name.trim();
    const email = form.email.trim();
    if (name.length < 2) {
      setFormError("Informe um nome com ao menos 2 caracteres.");
      return;
    }
    if (!EMAIL_RE.test(email)) {
      setFormError("Informe um e-mail válido.");
      return;
    }
    setFormError("");
    onCreateAndLink({
      name,
      email: email.toLowerCase(),
      role: form.role,
      ...(form.specialty.trim() ? { specialty: form.specialty.trim() } : {}),
    });
  };

  return (
    <Modal isOpen onClose={onClose} titleId={titleId} width="540px">
      <div className={styles.header}>
        <h2 id={titleId} className={styles.title}>
          Adicionar profissional
        </h2>
        <p className={styles.sub}>
          ao setor <strong>{department.name}</strong>
        </p>
      </div>

      <div className={styles.tabs} role="tablist" aria-label="Como adicionar">
        <button
          type="button"
          role="tab"
          aria-selected={mode === "existing"}
          className={mode === "existing" ? styles.tabActive : styles.tab}
          onClick={() => setMode("existing")}
        >
          Vincular existente
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === "new"}
          className={mode === "new" ? styles.tabActive : styles.tab}
          onClick={() => setMode("new")}
        >
          Criar novo
        </button>
      </div>

      <div className={styles.body}>
        {mode === "existing" ? (
          <>
            <div className={styles.search}>
              <TbSearch size={16} aria-hidden="true" />
              <input
                type="text"
                className={styles.searchInput}
                placeholder="Buscar por nome ou e-mail"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label="Buscar profissional"
              />
            </div>

            {availableMembers.length === 0 ? (
              <p className={styles.muted}>
                Todos os profissionais já estão neste setor. Use a aba
                &quot;Criar novo&quot; para cadastrar outro.
              </p>
            ) : filtered.length === 0 ? (
              <p className={styles.muted}>Nenhum profissional encontrado.</p>
            ) : (
              <ul className={styles.pickList}>
                {filtered.map((m) => {
                  const name = memberName(m);
                  return (
                    <li key={m.id}>
                      <button
                        type="button"
                        className={styles.pickRow}
                        onClick={() => onLinkExisting(m.id)}
                        disabled={busy}
                      >
                        <span
                          className={styles.avatar}
                          style={{ background: stringToColor(name) }}
                          aria-hidden="true"
                        >
                          {initialOf(name)}
                        </span>
                        <span className={styles.pickInfo}>
                          <span className={styles.pickName}>{name}</span>
                          <span className={styles.pickMeta}>
                            {m.specialty || m.user?.email || "—"}
                          </span>
                        </span>
                        <TbPlus
                          size={18}
                          className={styles.pickAdd}
                          aria-hidden="true"
                        />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        ) : (
          <form className={styles.form} onSubmit={handleCreate} noValidate>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="new-prof-name">
                Nome <span aria-hidden="true">*</span>
              </label>
              <input
                id="new-prof-name"
                name="name"
                type="text"
                className={styles.input}
                value={form.name}
                onChange={handleField}
                placeholder="Nome completo"
                maxLength={100}
                required
              />
            </div>

            <div className={styles.field}>
              <label className={styles.label} htmlFor="new-prof-email">
                E-mail <span aria-hidden="true">*</span>
              </label>
              <input
                id="new-prof-email"
                name="email"
                type="email"
                className={styles.input}
                value={form.email}
                onChange={handleField}
                placeholder="email@exemplo.com"
                maxLength={200}
                required
              />
            </div>

            <div className={styles.row}>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="new-prof-role">
                  Função
                </label>
                <select
                  id="new-prof-role"
                  name="role"
                  className={styles.input}
                  value={form.role}
                  onChange={handleField}
                >
                  <option value="MEMBER">Membro</option>
                  <option value="ADMIN">Admin</option>
                </select>
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="new-prof-specialty">
                  Especialidade
                </label>
                <input
                  id="new-prof-specialty"
                  name="specialty"
                  type="text"
                  className={styles.input}
                  value={form.specialty}
                  onChange={handleField}
                  placeholder="Opcional"
                  maxLength={100}
                />
              </div>
            </div>

            {formError && (
              <p className={styles.error} role="alert">
                {formError}
              </p>
            )}

            <p className={styles.hint}>
              Uma senha temporária será gerada e enviada por e-mail ao novo
              profissional.
            </p>

            <button
              type="submit"
              className={styles.primaryBtn}
              disabled={busy}
            >
              {busy ? "Criando..." : "Criar e adicionar ao setor"}
            </button>
          </form>
        )}
      </div>

      <div className={styles.footer}>
        <button
          type="button"
          className={styles.cancelBtn}
          onClick={onClose}
          disabled={busy}
        >
          Fechar
        </button>
      </div>
    </Modal>
  );
}
